import { useEffect, useMemo, useState } from 'react';
import { Loader2, ShieldCheck, RefreshCw, PlusCircle, CalendarClock, CheckCircle2, Clock, XCircle, Info } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader.jsx';
import { Card } from '../components/ui/Card.jsx';
import { Button } from '../components/ui/Button.jsx';
import { myLicenseApi } from '../api/sdx500.js';
import { overlayDismiss } from '../lib/overlayDismiss';

// 授權時間東訊給 UTC、以台灣當地午夜為界 → 顯示轉台北
function fmtDate(s) {
    if (!s) return '—';
    const str = String(s);
    if (!/[TZ]/.test(str)) return str.slice(0, 10);
    const d = new Date(str);
    if (isNaN(d.getTime())) return str.slice(0, 10);
    return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' });
}
function daysUntil(s) {
    if (!s) return null;
    const d = new Date(s);
    if (isNaN(d.getTime())) return null;
    return Math.ceil((d.getTime() - Date.now()) / 86400000);
}

const APP_STATUS = {
    pending: { label: '待處理', icon: Clock, cls: 'bg-warn-100 text-[#8a5e16]' },
    completed: { label: '已完成', icon: CheckCircle2, cls: 'bg-success-100 text-[#3f5733]' },
    rejected: { label: '未通過', icon: XCircle, cls: 'bg-danger-100 text-[#7a2c27]' },
};

export default function MyLicensePage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mode, setMode] = useState(null);   // 'renewal' | 'addon' | null
    const [syncing, setSyncing] = useState(false);
    const [syncMsg, setSyncMsg] = useState(null);   // { ok, text } 「從設備同步」的結果

    // 「重新整理」= 先向設備同步、再重讀資料庫（jacob 0909：不要兩顆按鈕，重新整理順便更新）。
    // 設備是權威；只重讀資料庫的話，設備上剛生效的授權永遠進不來。
    // 60 秒內按過 → 後端回 429 → 靜默跳過同步、照樣重讀；連不上設備 → 顯示一行小字，資料照舊顯示。
    async function refreshAll() {
        setSyncing(true); setSyncMsg(null);
        try {
            const r = await myLicenseApi.refresh();
            if (r.changed) setSyncMsg({ ok: true, text: '已從設備同步，授權已更新' });
        } catch (e) {
            if (e.status !== 429 && e.body?.error !== 'too_frequent') {
                setSyncMsg({ ok: false, text: e.body?.hint || e.body?.error || e.message });
            }
        }
        try { await load(); } finally { setSyncing(false); }
    }

    async function load() {
        setLoading(true); setError(null);
        try { setData(await myLicenseApi.get()); }
        catch (e) { setError(e.body?.hint || e.body?.error || e.message); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const aaEnd = data?.summary?.AA?.licenseEnd;
    const csEnd = data?.summary?.CS?.licenseEnd;
    const soonestDays = useMemo(() => {
        const ds = [daysUntil(aaEnd), daysUntil(csEnd)].filter((x) => x != null);
        return ds.length ? Math.min(...ds) : null;
    }, [aaEnd, csEnd]);
    const nearExpiry = soonestDays != null && soonestDays <= 90;
    const pendingByType = (t) => (data?.applications || []).some((a) => a.status === 'pending' && a.type === t);

    return (
        <div>
            <PageHeader
                eyebrow="TECOM SDX500 · 授權服務"
                title="授權續約 / 加購"
                description="查看您目前的 AA（智慧總機）/ CS（智慧客服）授權，合約快到期可申請續約、需要更多席次可申請加購。送出後由東訊授權管理人員處理。"
                actions={(
                    <Button variant="outline" onClick={refreshAll} disabled={loading || syncing}>
                        {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} 重新整理
                    </Button>
                )}
            />

            {syncMsg && (
                <div className={['text-[13px] rounded-lg px-4 py-2.5 mb-4 border', syncMsg.ok ? 'bg-success-100 border-success-500/30 text-[#3f5733]' : 'bg-danger-100/40 border-danger-500/30 text-danger-500'].join(' ')}>
                    {syncMsg.text}
                </div>
            )}
            {loading ? (
                <Card><div className="text-sm text-ink-400 italic text-center py-10 flex items-center justify-center gap-2"><Loader2 size={15} className="animate-spin" />載入中…</div></Card>
            ) : error ? (
                <Card className="bg-danger-100/40 border-danger-500/30"><div className="text-sm text-danger-500">{error}</div></Card>
            ) : !data ? null : (
                <div className="space-y-6">
                    {/* 到期提醒 */}
                    {nearExpiry && (
                        <div className="flex items-start gap-2.5 bg-warn-100 border border-[#e7cf9a] rounded-lg px-4 py-3 text-[13px] text-[#7a5512]">
                            <CalendarClock size={17} className="mt-0.5 shrink-0" />
                            <div>您的授權將在 <b>{soonestDays}</b> 天內到期（{fmtDate(soonestDays === daysUntil(aaEnd) ? aaEnd : csEnd)}）。建議儘早申請續約，避免服務中斷。</div>
                        </div>
                    )}

                    {/* 目前授權 */}
                    <Card>
                        <h3 className="heading-display text-lg font-semibold text-ink-900 mb-4">目前授權</h3>
                        <div className="grid sm:grid-cols-2 gap-4">
                            {['AA', 'CS'].map((t) => {
                                const s = data.summary?.[t] || { seats: 0, licenseEnd: null };
                                const d = daysUntil(s.licenseEnd);
                                return (
                                    <div key={t} className="border border-cream-200 rounded-lg p-4 bg-white">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[13px] font-medium text-ink-700">{t === 'AA' ? 'AA · 智慧總機' : 'CS · 智慧客服'}</span>
                                            <span className="heading-display text-2xl font-semibold text-ink-900">{s.seats}<span className="text-sm text-ink-400 font-normal"> 席</span></span>
                                        </div>
                                        <div className="mt-2 text-[12px] text-ink-500 font-mono flex items-center gap-1.5">
                                            <CalendarClock size={13} className="text-ink-400" />
                                            到期 {fmtDate(s.licenseEnd)}{d != null && <span className={d <= 90 ? 'text-[#8a5e16]' : 'text-ink-400'}>（{d} 天）</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex flex-wrap gap-2.5 mt-5">
                            <Button variant="accent" onClick={() => setMode('renewal')} disabled={pendingByType('renewal')}>
                                <RefreshCw size={15} /> {pendingByType('renewal') ? '續約申請處理中…' : '申請續約'}
                            </Button>
                            <Button variant="outline" onClick={() => setMode('addon')} disabled={pendingByType('addon')}>
                                <PlusCircle size={15} /> {pendingByType('addon') ? '加購申請處理中…' : '加購 AA / CS'}
                            </Button>
                        </div>
                    </Card>

                    {/* 申請記錄 */}
                    {(data.applications || []).length > 0 && (
                        <Card>
                            <h3 className="heading-display text-lg font-semibold text-ink-900 mb-4">申請記錄</h3>
                            <div className="space-y-2">
                                {data.applications.map((a) => {
                                    const st = APP_STATUS[a.status] || APP_STATUS.pending;
                                    const Icon = st.icon;
                                    const desc = a.type === 'renewal'
                                        ? `續約 ${a.renewYears} 年`
                                        : `加購 ${[a.addonAA ? `AA ${a.addonAA} 席` : '', a.addonCS ? `CS ${a.addonCS} 席` : ''].filter(Boolean).join('、')}`;
                                    return (
                                        <div key={a.id} className="flex items-center gap-3 border border-cream-100 rounded-lg px-3.5 py-2.5 bg-white">
                                            <span className={['inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11.5px] font-medium', st.cls].join(' ')}>
                                                <Icon size={12} /> {st.label}
                                            </span>
                                            <span className="text-[13px] text-ink-700 flex-1">{desc}</span>
                                            <span className="font-mono text-[11px] text-ink-400">{fmtDate(a.createdAt?._seconds ? new Date(a.createdAt._seconds * 1000).toISOString() : a.createdAt)}</span>
                                            {a.resolveNote && <span className="text-[11.5px] text-ink-400">· {a.resolveNote}</span>}
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>
                    )}
                </div>
            )}

            {mode && (
                <ApplyDrawer
                    mode={mode}
                    summary={data?.summary}
                    onClose={() => setMode(null)}
                    onDone={async () => { setMode(null); await load(); }}
                />
            )}
        </div>
    );
}

function ApplyDrawer({ mode, summary, onClose, onDone }) {
    const isRenewal = mode === 'renewal';
    const [renewYears, setRenewYears] = useState(1);
    const [addonAA, setAddonAA] = useState(0);
    const [addonCS, setAddonCS] = useState(0);
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);

    async function submit() {
        setErr(null);
        if (!isRenewal && addonAA <= 0 && addonCS <= 0) { setErr('加購請至少填 1 席 AA 或 CS'); return; }
        setSaving(true);
        try {
            await myLicenseApi.apply(isRenewal
                ? { type: 'renewal', renewYears: Number(renewYears) }
                : { type: 'addon', addonAA: Number(addonAA) || 0, addonCS: Number(addonCS) || 0, note });
            onDone();
        } catch (e) {
            setErr(e.body?.hint || e.body?.error || e.message);
        } finally { setSaving(false); }
    }

    return (
        <>
            <div className="fixed inset-0 bg-ink-900/30 z-40" {...overlayDismiss(onClose)} />
            <aside className="fixed top-0 right-0 h-full w-[460px] max-w-[94vw] bg-cream-50 border-l border-cream-200 shadow-2xl z-50 flex flex-col">
                <header className="px-6 pt-6 pb-4 border-b border-cream-200">
                    <div className="inline-flex items-center gap-2 mb-2">
                        <span className="size-1.5 rounded-full bg-accent-500" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">{isRenewal ? 'renewal' : 'add-on'}</span>
                    </div>
                    <h2 className="heading-display text-xl font-semibold text-ink-900">{isRenewal ? '申請續約' : '加購 AA / CS'}</h2>
                    <p className="text-[12.5px] text-ink-500 mt-1.5">送出後由東訊授權人員處理；核准後授權會自動更新，席次同步生效。</p>
                </header>

                <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5">
                    {isRenewal ? (
                        <div>
                            <label className="block text-[12.5px] font-medium text-ink-700 mb-2">續約年數</label>
                            <div className="flex gap-2">
                                {[1, 2, 3].map((y) => (
                                    <button key={y} type="button" onClick={() => setRenewYears(y)}
                                        className={['flex-1 py-2.5 rounded-md border text-sm font-medium transition',
                                            renewYears === y ? 'bg-accent-500 text-white border-accent-500' : 'bg-white text-ink-700 border-cream-300 hover:border-accent-300'].join(' ')}>
                                        {y} 年
                                    </button>
                                ))}
                            </div>
                            <p className="text-[11.5px] text-ink-400 mt-2">現有 AA {summary?.AA?.seats || 0} 席、CS {summary?.CS?.seats || 0} 席將一併續約。</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {[['AA', addonAA, setAddonAA, 'AA · 智慧總機'], ['CS', addonCS, setAddonCS, 'CS · 智慧客服']].map(([k, val, set, label]) => (
                                <div key={k}>
                                    <label className="block text-[12.5px] font-medium text-ink-700 mb-1.5">{label} 加購席次</label>
                                    <div className="flex items-center gap-2">
                                        <button type="button" onClick={() => set(Math.max(0, val - 1))} className="size-9 rounded-md border border-cream-300 bg-white text-ink-600 hover:bg-cream-100">−</button>
                                        <input type="number" min="0" value={val} onChange={(e) => set(Math.max(0, Number(e.target.value) || 0))}
                                            className="w-20 text-center py-2 text-sm font-mono border border-cream-200 rounded-md focus:outline-none focus:border-accent-500" />
                                        <button type="button" onClick={() => set(val + 1)} className="size-9 rounded-md border border-cream-300 bg-white text-ink-600 hover:bg-cream-100">＋</button>
                                        <span className="text-[12px] text-ink-400 ml-1">目前 {summary?.[k]?.seats || 0} 席</span>
                                    </div>
                                </div>
                            ))}
                            <div>
                                <label className="block text-[12.5px] font-medium text-ink-700 mb-1.5">備註（選填）</label>
                                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                                    className="w-full px-3 py-2 text-sm bg-white border border-cream-200 rounded-md focus:outline-none focus:border-accent-500" placeholder="例如：預計下季擴編" />
                            </div>
                        </div>
                    )}

                    <div className="flex items-start gap-2 bg-cream-100 border border-cream-200 rounded-lg px-3.5 py-3 text-[12px] text-ink-500">
                        <Info size={15} className="mt-0.5 shrink-0" />
                        這是「申請」，不會立即扣款或變更。東訊確認後才會實際開通並更新您的授權。
                    </div>
                    {err && <div className="text-[13px] text-danger-500">{err}</div>}
                </div>

                <footer className="px-6 py-4 border-t border-cream-200 flex gap-2.5 justify-end bg-cream-50">
                    <Button variant="outline" onClick={onClose} disabled={saving}>取消</Button>
                    <Button variant="accent" onClick={submit} disabled={saving}>
                        {saving ? <><Loader2 size={15} className="animate-spin" /> 送出中…</> : '送出申請'}
                    </Button>
                </footer>
            </aside>
        </>
    );
}
